// DeleteAccountDialog — GDPR right-to-be-forgotten flow.
//
// Soft-delete: marks a row in pending_account_deletion via the
// self_request_account_delete RPC. A daily cron (configured in
// Supabase) hard-deletes the auth.users row + cascades after 30 days.
//
// User must type-confirm with their email to avoid mis-clicks.

import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, CheckCircle2, Loader2, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { callRpc } from '@/lib/supabaseTyped';
import { toast } from '@/hooks/use-toast';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteAccountDialog({ open, onOpenChange }: Props) {
  const { user, signOut } = useAuth();
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduled, setScheduled] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setConfirmText(''); setReason(''); setError(null);
      setBusy(false); setScheduled(null);
    }
  }, [open]);

  const email = user?.email ?? '';
  const canConfirm = confirmText.trim().toLowerCase() === email.toLowerCase();

  const handleDelete = async () => {
    if (!canConfirm) return;
    setBusy(true); setError(null);
    try {
      const { data, error: rpcError } = await callRpc<{
        scheduled_at?: string;
      }>('self_request_account_delete', {
        _reason: reason.trim().length > 0 ? reason.trim() : null,
      });
      if (rpcError) throw rpcError;
      const sched =
        (Array.isArray(data) ? data[0]?.scheduled_at : data?.scheduled_at) ?? null;
      setScheduled(sched);
      toast({
        title: 'Kontot är schemalagt för radering',
        description: 'Du loggas ut. Logga in igen inom 30 dagar för att avbryta.',
      });
      // Give the user a beat to read the success state, then sign out.
      setTimeout(() => { void signOut(); }, 2_500);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-destructive" />
            Radera konto
          </DialogTitle>
          <DialogDescription>
            Detta startar en 30 dagars soft-delete. Efter det raderas allt permanent.
          </DialogDescription>
        </DialogHeader>

        {scheduled ? (
          <div className="space-y-3 rounded-md border border-emerald-400/40 bg-emerald-50/50 dark:bg-emerald-950/20 p-4 text-sm">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-semibold">
              <CheckCircle2 className="w-5 h-5" />
              Radering schemalagd
            </div>
            <p className="text-xs">
              Permanent radering: <strong>{new Date(scheduled).toLocaleDateString('sv-SE')}</strong>.
              Logga in inom 30 dagar för att avbryta. Du loggas nu ut.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-xs">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-semibold">Det här tar bort:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Alla dina arbetsflöden i molnet</li>
                  <li>Alla bifogade filer</li>
                  <li>Din profil, inställningar, AI-historik</li>
                  <li>Aktiva prenumerationer avslutas vid periodens slut (ingen återbetalning)</li>
                </ul>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="del-reason" className="text-xs">Anledning (valfritt — hjälper oss bli bättre)</Label>
              <Textarea
                id="del-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Vad fungerade inte?"
                className="text-xs"
                data-testid="delete-reason"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="del-confirm" className="text-xs">
                Skriv din e-postadress för att bekräfta: <code>{email}</code>
              </Label>
              <Input
                id="del-confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={email}
                className="font-mono text-xs"
                autoComplete="off"
                data-testid="delete-confirm-input"
              />
            </div>

            {error && (
              <div className="text-xs text-destructive p-2 rounded bg-destructive/10">
                {error}
              </div>
            )}
          </div>
        )}

        {!scheduled && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Avbryt
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!canConfirm || busy}
              data-testid="delete-confirm-action"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Radera mitt konto
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
